import { Suspense, lazy, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Button, Flex } from 'antd';

import dayjs from 'dayjs';
import isEmpty from 'lodash.isempty';

import axios from '../../axios';

import { capitalize, downloadCSV } from '../../helpers/common';
import { sortDates, sortNumbers, sortStrings } from '../../helpers/filter';
import { hasPermission } from '../../helpers/user';

import { DATE_FORMAT, EDIT_COMPONENT_TYPE } from '../../constants/common';
import { BLANK_FILTER_TEXT } from '../../constants/livestock';
import messages from '../../constants/messages';
import { PERMISSIONS } from '../../constants/permissions';
import { TABLE_CONFIG_COLUMNS, TABLE_CONFIG_KEYS } from '../../constants/table-configurations';
import { TREATMENT_CSV_TEMPLATE } from '../../constants/templates';
import { TREATMENT_CSV_VALIDATION_SCHEMA } from '../../constants/validation-schemas';

import { useModal } from '../../hooks/useModal';
import { useUnsavedChangesPrompt } from '../../hooks/useUnsavedChangesPrompt';

import {
  createTreatment,
  createTreatments,
  deleteTreatments,
  fetchTreatmentTypes,
  fetchTreatmentUOM,
  fetchTreatments
} from '../../redux/actions/treatment';
import { setSelectedFilters } from '../../redux/reducers/filters';
import { setSortedData } from '../../redux/reducers/treatment';

import AppTable from '../../components/AppTable';
import { COLUMN_SIZE } from '../../components/AppTable/constants';

import { errorToastHandler, infoToastHandler, successToastHandler, warnToastHandler } from '../action_notifier';
import { useFilterOptions } from './useFilterOptions';

const AppModal = lazy(() => import('../../components/AppModal'));
const CreateTreatmentModal = lazy(() => import('./CreateTreatmentModal'));
const UnsavedChangesModal = lazy(() => import('../../components/ui/UnsavedChangesModal'));
const UploadModal = lazy(() => import('../../components/Modals/UploadModal'));

const commonColumnProperties = {
  ellipsis: true,
  sortDirections: ['ascend', 'descend']
};

function TreatmentsTable(props) {
  const dispatch = useDispatch();
  const history = useHistory();

  const formRef = useRef(null);
  const formCreateRef = useRef(null);

  const params = useParams();

  const {
    error,
    loading,
    sortedData,
    treatmentTypesSelectOptions,
    treatmentUOMFilterOptions,
    treatmentUOMSelectOptions,
    treatments
  } = useSelector(state => state.treatment);
  const { selectedFilters } = useSelector(state => state.filters);
  const user = useSelector(state => state.user);

  const [createFormSubmittable, setCreateFormSubmittable] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [submittable, setSubmittable] = useState(false);
  const [state, setState] = useState({
    bulk_modal: false,
    csv_modal: false,
    filters: { farms: [], geofences: [], labels: [] },
    selectedIds: [],
    uploadSuccessMessage: ''
  });

  const [uploadSuccessModalVisible, openUploadSuccessModal, closeUploadSuccessModal] = useModal();
  const [updatedDataSource, setUpdatedDataSource] = useState(undefined);

  useEffect(() => {
    getTreatments();
    dispatch(fetchTreatmentUOM());
    dispatch(fetchTreatmentTypes());

    return () => {
      dispatch(setSelectedFilters({}));
    };
  }, [dispatch]);

  useEffect(() => {
    if (treatments?.length) {
      dispatch(setSortedData(treatments));
    }
  }, [treatments]);

  useEffect(() => {
    // on "Clear all filters" set to sortedData that used for calc filter options to original set of data
    if (isEmpty(filteredInfo) && treatments?.length) {
      dispatch(setSortedData(treatments));
    }
  }, [filteredInfo]);

  const toggleUploadModal = useCallback(() => {
    setState(prevState => ({ ...prevState, csv_modal: !prevState.csv_modal }));
    formRef.current?.resetFields();
  }, []);

  function prepareTreatmentData(data, livestockId) {
    return {
      ...data,
      livestock_id: parseInt(livestockId),
      repeat_date: data.repeat_date ? data.repeat_date.format(DATE_FORMAT.ISO_8601) : '',
      review_date: data.review_date ? data.review_date.format(DATE_FORMAT.ISO_8601) : '',
      treatment_date: data.treatment_date ? data.treatment_date.format(DATE_FORMAT.ISO_8601) : ''
    };
  }

  function createTreatmentForCurrentAnimal(data) {
    const newData = prepareTreatmentData(data, params.id);

    dispatch(createTreatment(newData));
  }

  function createTreatmentForSelectedAnimals(data) {
    const records = treatments?.filter(item => state.selectedIds.includes(item.id));
    const newData = records.map(record => prepareTreatmentData(data, record.livestock_id));

    dispatch(createTreatments(newData));
  }

  async function handleCreateTreatment() {
    if (!state.selectedIds?.length && !params.id) return;

    const values = await formCreateRef.current?.validateFields();
    state.selectedIds?.length && !params.id
      ? createTreatmentForSelectedAnimals(values)
      : createTreatmentForCurrentAnimal(values);

    toggleModal('bulk_modal');
    formCreateRef.current?.resetFields();
  }

  async function handleChangeTableData([record]) {
    if (!record.treatment_id) {
      infoToastHandler(messages.CREATE_EVENT('treatment'));
      setUpdatedDataSource([...treatments]);

      return;
    }

    const initRecord = treatments?.find(i => i.id === record.id);
    const newData = {
      ...record,
      dosage_uom: record.dosage_uom?.value,
      id: isNaN(record.treatment_id) ? null : record.treatment_id,
      is_positive: record.is_positive ? record.is_positive?.value : initRecord.is_positive,
      repeat_date: record.repeat_date ? dayjs(record.repeat_date).format(DATE_FORMAT.ISO_8601) : null,
      review_date: record.review_date ? dayjs(record.review_date).format(DATE_FORMAT.ISO_8601) : null,
      treatment_date: dayjs(record.treatment_date || initRecord.treatment_date).format(DATE_FORMAT.ISO_8601),
      treatment_type_id: record.treatment_type_name?.value ?? record.treatment_type_id
    };

    try {
      const response = await axios.put(`treatment/${record.treatment_id}`, newData);

      if (response.status === 200) {
        // await getTreatments();
        successToastHandler(messages.UPDATED);
      }
    } catch (error) {
      console.error(error);
    }
  }

  function deleteRecords() {
    const treatmentIds = treatments?.filter(d => state.selectedIds.includes(d.id)).map(i => i.treatment_id);
    dispatch(deleteTreatments(treatmentIds));

    if (!params.id) getTreatments();
    if (!error) successToastHandler(messages.ARCHIVED);

    toggleModal('delete_modal');
  }

  async function downloadCSVFile() {
    const ids = state.selectedIds;
    const url = ids?.length ? `/treatment/export?treatment_ids=${state?.selectedIds}` : '/treatment/template_export';

    try {
      const response = await axios.get(url, { responseType: 'blob' });

      downloadCSV(response.data, `Treatments_${dayjs().format(DATE_FORMAT.DATE)}.xlsx`);
    } catch (error) {
      errorToastHandler(messages.FAILED_ON_DOWNLOAD_CSV);
    }
  }

  async function getTreatments(query) {
    const animalId = params.id ? +params.id : null;
    const reqParams = animalId
      ? {
          livestock_ids: animalId,
          treatment_ids: animalId
        }
      : {
          query,
          farm_ids: state.filters?.farms?.map(x => x.value),
          label_ids: state.filters?.labels?.map(x => x.value),
          geofence_ids: state.filters?.geofences?.map(x => x.value)
        };
    try {
      await dispatch(fetchTreatments(reqParams)).unwrap();
    } catch (error) {
      errorToastHandler(error?.message || error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  function toggleModal(modal) {
    formCreateRef.current?.resetFields();
    setState(prevState => ({ ...prevState, [modal]: !prevState[modal] }));
  }

  const clearFilters = () => {
    setFilteredInfo({});
    dispatch(setSelectedFilters({}));
  };

  const setFilters = (pagination, filters) => {
    setFilteredInfo(filters);
    dispatch(setSelectedFilters(filters));
  };

  const handleTableChange = useCallback(
    (pagination, filters, sorter, { currentDataSource }) => {
      setFilters(pagination, filters);

      dispatch(setSortedData(currentDataSource));
    },
    [setFilteredInfo, dispatch]
  );

  function selectRecords(selected) {
    setState(prevState => ({
      ...prevState,
      selectedIds: selected,
      selectAll: prevState.selectedIds?.length === selected?.length
    }));
  }

  async function uploadCSV(csv) {
    try {
      const response = await axios.post('treatment/uploadCsv', { csv });

      if (response?.status === 200) {
        setState(prevState => ({ ...prevState, uploadSuccessMessage: response.data.message }));
        await getTreatments();
        toggleUploadModal();
        openUploadSuccessModal();
      }
    } catch (error) {
      errorToastHandler(error?.response?.data?.message || messages.DEFAULT_ERROR);
    }
  }

  const actions = useMemo(() => {
    const availableActions = [];

    if (hasPermission([PERMISSIONS.LIVESTOCK_TREATMENT.CREATE], user.permissions)) {
      availableActions.push(
        {
          label: (
            <Button
              color="default"
              size="small"
              variant="link"
              disabled={!params.id && !state.selectedIds?.length}
              onClick={() => toggleModal('bulk_modal')}>
              Add treatment
            </Button>
          )
        },
        {
          label: (
            <Button color="default" size="small" variant="link" onClick={toggleUploadModal}>
              Upload treatment
            </Button>
          )
        }
      );
    }

    if (hasPermission([PERMISSIONS.LIVESTOCK_TREATMENT.VIEW_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button color="default" size="small" variant="link" onClick={downloadCSVFile}>
            Download treatment
          </Button>
        )
      });
    }

    if (hasPermission([PERMISSIONS.LIVESTOCK_TREATMENT.DELETE_OWN], user.permissions)) {
      availableActions.push({
        label: (
          <Button
            color="default"
            size="small"
            variant="link"
            disabled={!state.selectedIds?.length}
            onClick={() => toggleModal('delete_modal')}>
            Archive treatment
          </Button>
        )
      });
    }

    availableActions.push({
      label: (
        <Button
          color="default"
          size="small"
          variant="link"
          disabled={!Object.values(selectedFilters)?.length}
          onClick={clearFilters}>
          Clear all filters
        </Button>
      )
    });

    return availableActions;
  }, [state.selectedIds, selectedFilters, user.permissions]);

  const columnFilterOptions = useFilterOptions({ data: sortedData, treatmentUOMFilterOptions });

  const columns = [
    {
      ...commonColumnProperties,
      title: 'Treatment ID',
      dataIndex: 'treatment_identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.TREATMENT_ID,
      filteredValue: filteredInfo.treatment_identifier || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.treatment_identifier, b.treatment_identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Treatment Type',
      dataIndex: 'treatment_type_name',
      configColumnKey: TABLE_CONFIG_COLUMNS.TREATMENT_TYPE,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: treatmentTypesSelectOptions
      },
      filteredValue: filteredInfo.treatment_type_name || null,
      filters: columnFilterOptions?.treatmentTypes,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) =>
        value === BLANK_FILTER_TEXT ? !record?.treatment_type_name : record?.treatment_type_name === value,
      sorter: (a, b) => sortStrings(a.treatment_type_name, b.treatment_type_name)
    },
    {
      ...commonColumnProperties,
      title: 'Treatment Date',
      dataIndex: 'treatment_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.TREATMENT_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER },
      filteredValue: filteredInfo.treatment_date || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATE) : null),
      sorter: (a, b) => sortDates(a.treatment_date, b.treatment_date)
    },
    {
      ...commonColumnProperties,
      title: 'Symptoms',
      dataIndex: 'symptoms',
      configColumnKey: TABLE_CONFIG_COLUMNS.SYMPTOMS,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.symptoms || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.symptoms, b.symptoms)
    },
    {
      ...commonColumnProperties,
      title: 'Is Positive',
      dataIndex: 'is_positive',
      configColumnKey: TABLE_CONFIG_COLUMNS.IS_POSITIVE,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: [
          { label: 'Yes', value: 1 },
          { label: 'No', value: 0 }
        ]
      },
      filteredValue: filteredInfo.is_positive || null,
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false }
      ],
      width: COLUMN_SIZE.SM,
      onFilter: (value, record) => (value ? record.is_positive === 1 : !record.is_positive),
      render: value => (value === 1 ? 'Yes' : 'No'),
      sorter: (a, b) => sortNumbers(a.is_positive, b.is_positive)
    },
    {
      ...commonColumnProperties,
      title: 'Treatment',
      dataIndex: 'treatment',
      configColumnKey: TABLE_CONFIG_COLUMNS.TREATMENT,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.treatment || null,
      rules: [{ required: true, message: 'Required.' }],
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.treatment, b.treatment)
    },
    {
      ...commonColumnProperties,
      title: 'Medication',
      dataIndex: 'medication',
      configColumnKey: TABLE_CONFIG_COLUMNS.MEDICATION,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.medication || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.medication, b.medication)
    },
    {
      ...commonColumnProperties,
      title: 'Dosage UoM',
      dataIndex: 'dosage_uom',
      configColumnKey: TABLE_CONFIG_COLUMNS.DOSAGE_UOM,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.SELECT,
        options: treatmentUOMSelectOptions
      },
      filteredValue: filteredInfo.dosage_uom || null,
      filters: columnFilterOptions.uom,
      width: COLUMN_SIZE.MD,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.dosage_uom : record?.dosage_uom === value),
      sorter: (a, b) => sortStrings(a.dosage_uom, b.dosage_uom)
    },
    {
      ...commonColumnProperties,
      title: 'Dosage Amount',
      dataIndex: 'dosage_amount',
      configColumnKey: TABLE_CONFIG_COLUMNS.DOSAGE_AMOUNT,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.dosage_amount || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortNumbers(a.dosage_amount, b.dosage_amount)
    },
    {
      ...commonColumnProperties,
      title: 'Vet',
      dataIndex: 'vet',
      configColumnKey: TABLE_CONFIG_COLUMNS.VET,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.vet || null,
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortStrings(a.vet, b.vet)
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      configColumnKey: TABLE_CONFIG_COLUMNS.COMMENTS,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.comments || null,
      searchable: true,
      width: COLUMN_SIZE.XXL,
      sorter: (a, b) => sortStrings(a.comments, b.comments)
    },
    {
      ...commonColumnProperties,
      title: 'Cost',
      dataIndex: 'cost',
      configColumnKey: TABLE_CONFIG_COLUMNS.COST,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT_NUMBER },
      editable: true,
      filteredValue: filteredInfo.cost || null,
      rules: [{ type: 'number', message: 'Must be number.' }],
      searchable: true,
      width: COLUMN_SIZE.MD,
      sorter: (a, b) => sortNumbers(a.cost, b.cost)
    },
    {
      ...commonColumnProperties,
      title: 'Repeat Date',
      dataIndex: 'repeat_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.REPEAT_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER, minDate: 'treatment_date', allowClear: true },
      filteredValue: filteredInfo.repeat_date || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATE) : null),
      sorter: (a, b) => sortDates(a.repeat_date, b.repeat_date)
    },
    {
      ...commonColumnProperties,
      title: 'Review Date',
      dataIndex: 'review_date',
      configColumnKey: TABLE_CONFIG_COLUMNS.REVIEW_DATE,
      editConfig: { type: EDIT_COMPONENT_TYPE.DATE_PICKER, minDate: 'treatment_date', allowClear: true },
      filteredValue: filteredInfo.review_date || null,
      searchable: true,
      width: COLUMN_SIZE.LG,
      render: value => (value ? dayjs(value).format(DATE_FORMAT.DATE) : null),
      sorter: (a, b) => sortDates(a.review_date, b.review_date)
    },
    {
      ...commonColumnProperties,
      title: 'Result',
      dataIndex: 'result',
      configColumnKey: TABLE_CONFIG_COLUMNS.RESULT,
      editConfig: { type: EDIT_COMPONENT_TYPE.INPUT },
      editable: true,
      filteredValue: filteredInfo.result || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.result, b.result)
    }
  ];
  const livestockColumns = [
    {
      ...commonColumnProperties,
      title: 'Livestock ID',
      dataIndex: 'identifier',
      configColumnKey: TABLE_CONFIG_COLUMNS.LIVESTOCK_ID,
      filteredValue: filteredInfo.identifier || null,
      fixed: 'left',
      searchable: true,
      width: COLUMN_SIZE.MD,
      render: (value, record) => <Link to={`animal/${record.id}`}>{record.identifier}</Link>,
      sorter: (a, b) => sortStrings(a.identifier, b.identifier)
    },
    {
      ...commonColumnProperties,
      title: 'Mgmt Tag ID',
      dataIndex: 'eartag_management_id',
      configColumnKey: TABLE_CONFIG_COLUMNS.MGMT_TAG_ID,
      filteredValue: filteredInfo.eartag_management_id || null,
      searchable: true,
      width: COLUMN_SIZE.XL,
      sorter: (a, b) => sortStrings(a.eartag_management_id, b.eartag_management_id)
    },
    {
      ...commonColumnProperties,
      title: 'Sex',
      dataIndex: 'sex',
      configColumnKey: TABLE_CONFIG_COLUMNS.SEX,
      filteredValue: filteredInfo.sex || null,
      filters: columnFilterOptions?.sex,
      width: COLUMN_SIZE.LG,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.sex : record?.sex === value),
      render: value => capitalize(value),
      sorter: (a, b) => sortStrings(a.sex, b.sex)
    },
    {
      ...commonColumnProperties,
      title: 'Colour',
      dataIndex: 'colour',
      configColumnKey: TABLE_CONFIG_COLUMNS.COLOUR,
      editConfig: {
        type: EDIT_COMPONENT_TYPE.INPUT
      },
      filteredValue: filteredInfo.colour || null,
      searchable: true,
      sortDirections: ['ascend', 'descend'],
      width: COLUMN_SIZE.LG,
      sorter: (a, b) => sortStrings(a.colour, b.colour)
    },
    {
      ...commonColumnProperties,
      title: 'Breed',
      dataIndex: 'breed',
      configColumnKey: TABLE_CONFIG_COLUMNS.BREED,
      filteredValue: filteredInfo.breed || null,
      filters: columnFilterOptions?.breeds,
      width: COLUMN_SIZE.XL,
      onFilter: (value, record) => (value === BLANK_FILTER_TEXT ? !record?.breed : record?.breed === value),
      sorter: (a, b) => sortStrings(a.breed, b.breed)
    }
  ];

  const { NavigationPrompt, modalProps, nextLocation, confirmed, triggerUnsavedChangesPrompt } =
    useUnsavedChangesPrompt({
      when: isFormDirty,
      onSaveAndLeave: async () => {
        if (createFormSubmittable) {
          await handleCreateTreatment();

          return true;
        } else {
          warnToastHandler(messages.DATA_REQUIRED);
          formCreateRef.current?.validateFields();

          return false;
        }
      },
      onDiscardAndLeave: () => {
        formCreateRef.current?.resetFields();
        setIsFormDirty(false);
        setState(prev => ({ ...prev, bulk_modal: false }));
      }
    });

  useEffect(() => {
    if (!isFormDirty && nextLocation) {
      history.push(`${nextLocation.pathname}${nextLocation.search}`);
    }
  }, [isFormDirty, nextLocation, history]);

  const handleCancel = useCallback(() => {
    isFormDirty ? triggerUnsavedChangesPrompt() : setState(prev => ({ ...prev, bulk_modal: false }));
  }, [isFormDirty]);

  const tableColumns = params.id ? [...columns] : [...livestockColumns, ...columns];

  return (
    <>
      {NavigationPrompt}
      <AppTable
        headerClass="py-2"
        searchPlaceholder="Search treatments"
        actions={actions}
        baseColumns={tableColumns}
        dataSource={treatments}
        description={props.breadcrumb}
        loading={loading}
        searchable={props.searchable}
        tableConfigKey={TABLE_CONFIG_KEYS.LIVESTOCK_TREATMENTS}
        title={props.title}
        handleOnChange={handleTableChange}
        handleOnSelectRecords={selectRecords}
        handleSearchChange={getTreatments}
        updateTableData={handleChangeTableData}
        updatedDataSource={updatedDataSource}
      />

      <Suspense fallback={null}>
        <CreateTreatmentModal
          data={{ treatmentTypesSelectOptions, treatmentUOMSelectOptions }}
          formRef={formCreateRef}
          isOpen={state.bulk_modal}
          submittable={createFormSubmittable}
          handleCancel={handleCancel}
          handleConfirm={handleCreateTreatment}
          onChange={setIsFormDirty}
          setCreateFormSubmittable={setCreateFormSubmittable}
        />

        <AppModal
          isOpen={state.delete_modal}
          confirmButtonColor="danger"
          confirmButtonText="Archive"
          title="Archive treatment"
          handleCancel={() => toggleModal('delete_modal')}
          handleConfirm={deleteRecords}>
          <div className="py-4">{messages.ARCHIVE_CONFIRMATION('treatments')}</div>
        </AppModal>

        <UploadModal
          fileName="treatment-template.csv"
          formRef={formRef}
          isOpen={state.csv_modal}
          schema={TREATMENT_CSV_VALIDATION_SCHEMA}
          submittable={submittable}
          template={TREATMENT_CSV_TEMPLATE}
          onCancel={toggleUploadModal}
          onUpload={uploadCSV}
          setSubmittable={setSubmittable}
          description={
            <>
              <p className="mb-0">You can download the treatment list to input new treatments.</p>
              <p className="mb-0">Download and save the file and then choose this file to upload new treatments.</p>
            </>
          }
        />

        <AppModal
          confirmButtonText="Ok"
          cancelButtonHidden={true}
          isOpen={uploadSuccessModalVisible}
          handleConfirm={closeUploadSuccessModal}>
          <Flex align="center" justify="center" className="pb-2 pt-5">
            {state.uploadSuccessMessage}
          </Flex>
        </AppModal>

        <UnsavedChangesModal
          isOpen={modalProps.visible}
          handleCancel={modalProps.onCancel}
          handleConfirm={modalProps.onSave}
          handleDiscard={modalProps.onDiscard}
        />
      </Suspense>
    </>
  );
}

export default memo(TreatmentsTable);
